import type { DoctorRole } from '@prisma/client';

import type { ProcedureGroup } from '~/config/referralReasons';
import { specialtyReasons } from '~/config/referralReasons';
import { getSpecialtyConfig } from '~/config/specialties';
import { ReferralType } from '~/types/referral';
import type { DiagnosisOption, DropdownOption, SpecialtyFormConfig } from '~/types/specialty';

import { formatCustomReasonCode } from './referral-helpers';
import { isDentistOrLegacyDentistRole } from './role-helpers';
import { formatToothOrAreaWithInitials } from './tooth-helpers';

/**
 * Standard date format string used throughout the application.
 * Used by react-datepicker components and date formatting functions.
 * Format: MM/dd/yyyy (e.g., 01/15/2024)
 */
export const STANDARD_DATE_FORMAT = 'MM/dd/yyyy';
export const APP_TIME_ZONE = 'America/New_York';

/**
 * Formats a doctor's full name as "FirstName LastName" — no "Dr." prefix.
 *
 * @param firstName - The doctor's first name (may include middle initial)
 * @param lastName - The doctor's last name
 * @param role - @deprecated Unused. Kept in the signature for backward compatibility with existing callers; will be removed in a follow-up cleanup PR.
 *
 * @example
 * formatDoctorName('John', 'Smith') → 'John Smith'
 * formatDoctorName('Jane', 'Doe', 'OWNER_DENTIST') → 'Jane Doe' (role ignored)
 */
export function formatDoctorName(
  firstName: string,
  lastName: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  role?: DoctorRole | string | null
): string {
  return `${firstName} ${lastName}`;
}

/**
 * Formats a doctor's full name with optional degree appended after a comma.
 * No "Dr." prefix is added.
 *
 * @param firstName - The doctor's first name (may include middle initial)
 * @param lastName - The doctor's last name
 * @param role - @deprecated Unused (kept for backward compatibility — see formatDoctorName).
 * @param degree - Optional degree to append (e.g., "DMD", "DDS, MS", "DDS, MD, FACD")
 * @returns Name in the form "FirstName LastName" or "FirstName LastName, Degree"
 *
 * @example
 * formatDoctorNameWithDegree('John', 'Smith', undefined, 'DDS') → 'John Smith, DDS'
 * formatDoctorNameWithDegree('Sam', 'Patel', undefined, 'DDS, MD, FACD') → 'Sam Patel, DDS, MD, FACD'
 * formatDoctorNameWithDegree('Bob', 'Johnson') → 'Bob Johnson'
 */
export function formatDoctorNameWithDegree(
  firstName: string,
  lastName: string,
  role?: DoctorRole | string | null,
  degree?: string | null
): string {
  const formattedName = formatDoctorName(firstName, lastName, role);

  if (degree) {
    return `${formattedName}, ${degree}`;
  }

  return formattedName;
}

/**
 * Like formatDoctorNameWithDegree, but prefixes "Dr." only when the role is an
 * actual dentist role. A referring/receiving "doctor" profile is sometimes
 * office staff (front desk, office manager, etc.) — e.g. a non-dentist who
 * submitted a referral via an embedded form or manual entry — and giving them
 * an unearned "Dr." title misrepresents them.
 *
 * @example
 * formatDoctorTitleName('Jane', 'Smith', 'OWNER_DENTIST', 'DDS') → 'Dr. Jane Smith, DDS'
 * formatDoctorTitleName('Sylvia', 'Williams', 'OFFICE_MANAGER') → 'Sylvia Williams'
 */
export function formatDoctorTitleName(
  firstName: string,
  lastName: string,
  role?: DoctorRole | string | null,
  degree?: string | null
): string {
  const formattedName = formatDoctorNameWithDegree(firstName, lastName, role, degree);
  return isDentistOrLegacyDentistRole(String(role ?? '')) ? `Dr. ${formattedName}` : formattedName;
}

export const formatSpecialtyData = (specialtyData: { diagnosis?: string[] }): string => {
  const { diagnosis = [] } = specialtyData;

  if (diagnosis.length === 0) {
    return '';
  }

  const formatSection = (title: string, items: string[]): string => {
    if (items.length === 0) return '';
    return `\n${title}: \n${items.map(item => `- ${item}`).join('\n')}`;
  };

  return [
    '\n\n--- SPECIALTY-SPECIFIC DATA ---',
    formatSection('Diagnosis', diagnosis),
    '\n--- END SPECIALTY DATA ---\n',
  ].join('');
};

/**
 * Removes the specialty data markers from referral notes.
 * This includes the "--- SPECIALTY-SPECIFIC DATA ---" headers and footers
 * that are automatically added when creating a referral.
 *
 * @param notes The original notes text with potential specialty data markers
 * @returns The cleaned notes without the markers
 */
export const cleanNotesForDisplay = (notes: string | null): string => {
  if (!notes) return '';

  // Define the markers we want to remove
  const startMarker = '--- SPECIALTY-SPECIFIC DATA ---';
  const endMarker = '--- END SPECIALTY DATA ---';

  // Split the notes by the start marker
  const parts = notes.split(startMarker);

  if (parts.length === 1) {
    // No specialty data markers found
    return notes;
  }

  // The first part is the regular notes
  let cleanedNotes = parts[0].trim();

  // If there's specialty data, extract it without the markers
  if (parts.length > 1) {
    const specialtyDataWithEnd = parts[1];
    const specialtyDataParts = specialtyDataWithEnd.split(endMarker);

    if (specialtyDataParts.length > 1) {
      // Extract the specialty data content without the end marker
      const specialtyData = specialtyDataParts[0].trim();
      // Extract anything that might be after the end marker
      const afterEndMarker = specialtyDataParts[1].trim();

      // Add the specialty data content if it's not empty
      if (specialtyData) {
        cleanedNotes += '\n\n' + specialtyData;
      }

      // Add any content that was after the end marker
      if (afterEndMarker) {
        cleanedNotes += '\n\n' + afterEndMarker;
      }
    } else {
      // If we didn't find the end marker, just include everything
      cleanedNotes += '\n\n' + specialtyDataWithEnd.trim();
    }
  }

  return cleanedNotes;
};

/**
 * Parses a date string (YYYY-MM-DD or ISO string) in local timezone instead of UTC.
 * This prevents date shifts when dates are parsed from strings.
 * Handles both "2025-12-30" and "2025-12-30T00:00:00.000Z" formats.
 *
 * @param dateString - The date string in YYYY-MM-DD or ISO format
 * @returns Date object in local timezone, or null if invalid
 */
export const parseLocalDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;

  try {
    // Extract just the date part (YYYY-MM-DD) from ISO strings or plain date strings
    // This handles both "2025-12-30" and "2025-12-30T00:00:00.000Z" formats
    const datePart = dateString.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return null;

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

    // Create date in local timezone (not UTC)
    // This ensures "2025-12-30" becomes Dec 30 in local time, not UTC midnight
    const date = new Date(year, month, day);

    // Validate the date is correct (handles invalid dates like Feb 30)
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      return null;
    }

    return date;
  } catch (error) {
    return null;
  }
};

/**
 * Formats a date to YYYY-MM-DD in local timezone (not UTC).
 * This ensures dates selected by users are displayed consistently.
 *
 * @param date - The date to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateToLocalString = (date: Date | null): string => {
  if (!date) return '';

  try {
    if (isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return '';
  }
};

/**
 * Returns the last millisecond of the given calendar day in local timezone.
 */
export const endOfLocalDay = (date: Date): Date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
};

// Format date to MM/dd/yyyy (uses local timezone)
export const formatDate = (date: Date | string | null | undefined): string => {
  return formatToStandardDate(date);
};

/**
 * Formats a date to MM/dd/yyyy format using local timezone.
 *
 * @param date - The date to format
 * @returns Formatted date string in MM/dd/yyyy format
 */
export const formatToStandardDate = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      timeZone: APP_TIME_ZONE,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(d);
  } catch {
    return '';
  }
};

/**
 * Formats a date with time for audit trail display (e.g. "3/9/2025, 2:30 PM").
 *
 * @param date - The date to format
 * @returns Formatted date and time string
 */
export const formatToStandardDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    return new Intl.DateTimeFormat('en-US', {
      timeZone: APP_TIME_ZONE,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return '';
  }
};

export const formatToShortDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    return new Intl.DateTimeFormat('en-US', {
      timeZone: APP_TIME_ZONE,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return '';
  }
};

/**
 * Formats a date for the referral Activity Log.
 * Output shape: M/D/YYYY h:mm am/pm (no comma, no leading zeros on month/day/hour, lowercase am/pm).
 * Examples: "4/28/2026 9:03 am", "1/5/2026 12:00 pm".
 */
export const formatActivityLogTimestamp = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: APP_TIME_ZONE,
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(d);

    const get = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find(p => p.type === type)?.value ?? '';

    const month = get('month');
    const day = get('day');
    const year = get('year');
    const hour = get('hour');
    const minute = get('minute');
    const dayPeriod = get('dayPeriod').toLowerCase();

    return `${month}/${day}/${year} ${hour}:${minute} ${dayPeriod}`;
  } catch {
    return '';
  }
};

/**
 * Formats a date-only value (like DoB) to MM/dd/yyyy format using UTC methods.
 * This prevents timezone conversion issues that can shift the date by one day.
 * Use this function for date-only values that should not be affected by timezone conversions.
 *
 * @param date - The date to format (should be a date-only value like DoB)
 * @returns Formatted date string in MM/dd/yyyy format
 */
export const formatDateOnly = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    // Use UTC methods to avoid timezone conversion for date-only values
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const year = String(d.getUTCFullYear());
    return `${month}/${day}/${year}`;
  } catch (error) {
    return '';
  }
};

/**
 * Formats a date to "MM/dd/yyyy" format using local timezone.
 *
 * @param date - The date to format
 * @returns Formatted date string
 */
export const formatDateDayMonthYear = (date: Date | string | null | undefined): string => {
  return formatToStandardDate(date);
};

/**
 * Format a date string to a human-readable format (MM/dd/yyyy)
 * @param dateString The date string to format
 * @returns Formatted date string
 */
export const formatDateValue = (dateString: string | null): string => {
  if (!dateString) return 'Not specified';
  const formatted = formatToStandardDate(dateString);
  return formatted || dateString;
};

/**
 * Formats a US phone number for display/input as XXX-XXX-XXXX (e.g. 919-696-3044).
 * Strips non-digits, caps at 10 digits, adds hyphens as the user types.
 */
export const formatPhoneString = (phone: string | null | undefined): string => {
  if (!phone) return '';

  const cleaned = phone.replace(/\D/g, '');
  const limited = cleaned.slice(0, 10);

  if (limited.length === 0) return '';
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
};

export const PHONE_REGEX = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

/**
 * Computes the next formatted value for a phone `<input>`'s onChange, fixing a common
 * phone-mask bug: since `formatPhoneString` rebuilds hyphens purely from digit count,
 * Backspace right after an auto-inserted hyphen is a no-op — the hyphen is removed by the
 * browser, but the digit count (and therefore the re-formatted string) is unchanged, so it
 * looks like nothing happened and the user has to press Backspace twice to delete one digit.
 * When that exact case is detected (input got shorter but digit count didn't change), this
 * drops one more digit so a single Backspace always removes one digit.
 */
export const nextPhoneInputValue = (previousFormatted: string, rawInput: string): string => {
  const prevDigits = previousFormatted.replace(/\D/g, '');
  const nextDigits = rawInput.replace(/\D/g, '');
  const isDeletion = rawInput.length < previousFormatted.length;
  if (isDeletion && nextDigits.length > 0 && nextDigits.length === prevDigits.length) {
    return formatPhoneString(nextDigits.slice(0, -1));
  }
  return formatPhoneString(rawInput);
};

// Convert phone number to E.164 format for AWS Cognito
export const formatPhoneForCognito = (phone: string | null | undefined): string | null => {
  if (!phone) return null;

  const cleaned = phone.replace(/\D/g, ''); // Remove all non-digits

  if (cleaned.length !== 10) return null; // Must be exactly 10 digits for US numbers

  return `+1${cleaned}`; // Add US country code
};

// Redact sensitive information from error messages
export const stripPHI = (text: string): string => {
  if (!text) return '';

  // Replace patient names with [REDACTED]
  return text.replace(/patient\s+([a-zA-Z]+\s+[a-zA-Z]+)/i, '[REDACTED]');
};

/**
 * Formats a dental referral summary in a dentist-friendly format.
 * Uses structured format with explicit "Diagnosis:" labels and colon-separated procedures.
 *
 * @param referralData Object containing the referral details
 * @returns A formatted string summary of the referral
 *
 * @example
 * // Returns: "NSRCT
 * // Diagnosis: #3 Irreversible Pulpitis with Symptomatic Apical Periodontitis
 * // Restore Access: Permanent Restoration
 * // Tooth is Non Restorable: Please Manage"
 */
export const formatDentalReferralSummary = (referralData: {
  reasonGroups: Array<{
    selectedTeeth: number[];
    reasonCode: string;
    reasonLabel?: string | null;
    diagnosis?: string[];
    notes?: string | null;
    dateValues?: Record<string, string | null>;
    selectedProceduresByGroup?: Record<string, string | string[]> | null;
  }>;
  specialty: string;
  referralType: string;
}): string => {
  const knownAcronyms: Record<string, string> = {
    non_surgical_root_canal_treatment: 'NSRCT',
    nsrct: 'NSRCT',
    tmj: 'TMJ',
    temporomandibular_joint: 'TMJ',
  };

  // Helper to clean and format reason codes
  const formatReasonCode = (code: string): string => {
    // knownAcronyms wins so summary stays concise (e.g. NSRCT) even when the
    // configured label is verbose.
    if (knownAcronyms[code]) {
      return knownAcronyms[code];
    }

    const customLabel = formatCustomReasonCode(code);
    if (customLabel) {
      return customLabel;
    }

    // Otherwise prefer the configured label so renames in referralReasons.ts
    // propagate here without needing per-code overrides.
    const reasonsForSpecialty = specialtyReasons[referralData.specialty];
    const configuredLabel = reasonsForSpecialty?.find(r => r.value === code)?.label;
    if (configuredLabel) {
      return configuredLabel;
    }

    return code
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper to clean diagnosis text
  const cleanDiagnosis = (diagnosis: string): string => {
    // Remove any field name that ends with "Diagnosis:" (e.g., "Pulpal Diagnosis:", "Periodontal Diagnosis:")
    // Also remove "(Optional)" text if present
    return diagnosis.replace(/^.*Diagnosis:\s*/i, '').replace(/\s*\(Optional\)\s*/i, '');
  };

  const formatSpecialtyConfigProceduresColon = (
    selectedProceduresByGroup: Record<string, string | string[]>,
    specialty: string
  ): string[] => {
    const formattedProcedures: string[] = [];

    Object.entries(selectedProceduresByGroup).forEach(([groupName, procedureValue]) => {
      let procedureGroup: ProcedureGroup | DiagnosisOption | null = null;

      if (specialtyReasons[specialty]) {
        for (const reason of specialtyReasons[specialty]) {
          if (reason.procedures) {
            const foundGroup = reason.procedures.find(pg => pg.name === groupName);
            if (foundGroup) {
              procedureGroup = foundGroup;
              break;
            }
          }
        }
      }

      // Fall back to specialty-level treatment questions (e.g. endodontic
      // "Restore Access" / "Tooth is Non Restorable").
      if (!procedureGroup) {
        procedureGroup =
          getSpecialtyConfig(specialty)?.treatmentQuestions?.find(q => q.name === groupName) ??
          null;
      }

      if (!procedureGroup) return;

      const optionsArray = Array.isArray(procedureGroup.options) ? procedureGroup.options : [];

      const valuesToProcess = Array.isArray(procedureValue) ? procedureValue : [procedureValue];

      const procedureLabels: string[] = [];
      valuesToProcess.forEach(value => {
        let label: string | undefined;

        if (typeof value === 'string') {
          const option = optionsArray.find(
            opt => typeof opt === 'object' && 'value' in opt && opt.value === value
          ) as DropdownOption | undefined;

          if (option) {
            label = option.label;
          } else {
            const stringOption = optionsArray.find(opt => typeof opt === 'string' && opt === value);
            if (stringOption) {
              label = stringOption as string;
            } else {
              label = value;
            }
          }
        }

        if (label) {
          procedureLabels.push(label);
        }
      });

      if (procedureLabels.length > 0) {
        // Skip "Surgical Procedures" and "Non-Surgical Options" labels - just show the procedure values
        if (groupName === 'Surgical Procedures' || groupName === 'Non-Surgical Options') {
          formattedProcedures.push(procedureLabels.join(', '));
        } else {
          const shorterGroupName = groupName.replace('completed on tooth of interest', 'completed');
          formattedProcedures.push(`${shorterGroupName}: ${procedureLabels.join(', ')}`);
        }
      }
    });

    return formattedProcedures;
  };

  // Helper to format date values
  const formatDateValues = (dateValues: Record<string, string | null> | any): string[] => {
    const parsedDateValues = getDateValues(dateValues);
    if (Object.keys(parsedDateValues).length === 0) return [];

    return Object.entries(parsedDateValues)
      .map(([key, value]) => {
        if (!value) return '';

        // Convert key from camelCase to Title Case
        const fieldName = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/Date$/, '');

        return `${fieldName}: ${formatDateValue(value)}`;
      })
      .filter(Boolean);
  };

  // Collect date values - deduplicate only "Last Dental Cleaning" dates, keep all others
  let hasLastCleaningDate = false; // Track if we've already added a "Last Dental Cleaning" date
  const otherDateValues: string[] = []; // All other dates (no deduplication)

  // Process each reason group
  const summaries = referralData.reasonGroups.map(group => {
    const lines: string[] = [];

    // For "other" reason, use the notes field if available, otherwise use formatted reason code
    let reasonStr;
    if (group.reasonCode === 'other' && group.notes?.trim()) {
      reasonStr = group.notes.trim();
    } else if (group.reasonLabel?.trim()) {
      reasonStr = group.reasonLabel.trim();
    } else {
      reasonStr = formatReasonCode(group.reasonCode);
    }
    // Inline-summary groups (e.g. hospital "Treatment Plan", #1169) fold the
    // reason, the selected option, and the teeth into one line. Config comes
    // from the platform catalog — office-custom groups don't inline.
    const reasonConfig = specialtyReasons[referralData.specialty]?.find(
      r => r.value === group.reasonCode
    );
    const inlineGroupConfig = reasonConfig?.procedures?.find(pg => pg.inlineSummary);
    const inlineRaw = inlineGroupConfig
      ? group.selectedProceduresByGroup?.[inlineGroupConfig.name]
      : undefined;
    const inlineValue = Array.isArray(inlineRaw) ? inlineRaw[0] : inlineRaw;
    const inlineOptionLabel =
      inlineGroupConfig && inlineValue
        ? (inlineGroupConfig.options.find(o => o.value === inlineValue)?.label ?? inlineValue)
        : undefined;

    if (inlineOptionLabel) {
      const teethStr = (group.selectedTeeth ?? [])
        .slice()
        .sort((a, b) => a - b)
        .map(tooth => formatToothOrAreaWithInitials(tooth))
        .join(', ');
      lines.push([reasonStr, inlineOptionLabel, teethStr].filter(Boolean).join(' '));
      if (group.diagnosis && group.diagnosis.length > 0) {
        lines.push(`Diagnosis: ${group.diagnosis.map(cleanDiagnosis).join(' with ')}`);
      }
    } else {
      lines.push(reasonStr);

      // Add diagnoses if present with "Diagnosis:" prefix
      if (group.diagnosis && group.diagnosis.length > 0) {
        const cleanedDiagnoses = group.diagnosis.map(cleanDiagnosis);
        const diagnosisText = cleanedDiagnoses.join(' with ');

        if (group.selectedTeeth && group.selectedTeeth.length > 1) {
          // For multiple teeth, show diagnosis once after all teeth
          lines.push(`Diagnosis: ${diagnosisText}`);
        } else if (group.selectedTeeth && group.selectedTeeth.length === 1) {
          const toothStr = formatToothOrAreaWithInitials(group.selectedTeeth[0]);
          lines.push(`Diagnosis: ${toothStr} ${diagnosisText}`);
        } else {
          lines.push(`Diagnosis: ${diagnosisText}`);
        }
      } else if (group.selectedTeeth && group.selectedTeeth.length > 0) {
        // No diagnosis but multiple teeth - show the tooth numbers
        const toothNumbers = group.selectedTeeth
          .sort((a, b) => a - b)
          .map(tooth => formatToothOrAreaWithInitials(tooth))
          .join(', ');
        lines.push(toothNumbers);
      }
    }

    // Check for selected procedure in specialtyReasons
    if (group.notes) {
      const procedureMatch = group.notes.match(/Selected procedure: (.+)$/m);
      if (procedureMatch) {
        lines.push(`${reasonStr}: ${procedureMatch[1]}`);
      }
    }

    // Handle specialty config procedures from selectedProceduresByGroup,
    // excluding any group already folded into the inline summary line above.
    const proceduresForColonFormat = { ...(group.selectedProceduresByGroup || {}) };
    if (inlineOptionLabel && inlineGroupConfig) {
      delete proceduresForColonFormat[inlineGroupConfig.name];
    }
    if (Object.keys(proceduresForColonFormat).length > 0) {
      const specialtyConfigProcs = formatSpecialtyConfigProceduresColon(
        proceduresForColonFormat,
        referralData.specialty
      );
      lines.push(...specialtyConfigProcs);
    }

    // Add user-entered Additional Notes, except when reason is "other"
    // (notes is already used as the reason text) or when notes hold the
    // legacy "Selected procedure: ..." marker (rendered separately above).
    const trimmedNotes = group.notes?.trim();
    if (
      group.reasonCode !== 'other' &&
      trimmedNotes &&
      !trimmedNotes.startsWith('Selected procedure: ')
    ) {
      lines.push(`Notes: ${trimmedNotes}`);
    }

    // Collect date values - deduplicate only "Last Dental Cleaning" dates (by field name, not date value)
    if (group.dateValues) {
      const formattedDates = formatDateValues(group.dateValues);
      formattedDates.forEach(dateLine => {
        if (!dateLine) return;

        // Check if this is a "Last Dental Cleaning" date (field name after formatting)
        if (dateLine.startsWith('Last Dental Cleaning')) {
          // Deduplicate "Last Dental Cleaning" dates - only add once regardless of date value
          if (!hasLastCleaningDate) {
            hasLastCleaningDate = true;
            otherDateValues.push(dateLine);
          }
        } else {
          // Keep all other dates without deduplication
          otherDateValues.push(dateLine);
        }
      });
    }

    return lines.join('\n');
  });

  // Combine all summaries
  let combinedSummary = summaries.join('\n\n');

  // Add all date values at the end if any exist
  if (otherDateValues.length > 0) {
    combinedSummary += `\n\n${otherDateValues.join('\n')}`;
  }

  return combinedSummary;
};

/**
 * Safely parse dateValues from either a JSON string or object
 * @param dateValues The date values from the database (can be a JSON string or object)
 * @returns Parsed Record<string, string | null> or empty object if parsing fails
 */
export const getDateValues = (dateValues: any): Record<string, string | null> => {
  if (!dateValues) return {};

  // If it's already an object, return as is
  if (typeof dateValues === 'object' && !Array.isArray(dateValues)) {
    return dateValues;
  }

  // If it's a JSON string, parse it
  try {
    if (typeof dateValues === 'string') {
      return JSON.parse(dateValues);
    }
  } catch (e) {
    console.error('Failed to parse dateValues:', e);
  }

  return {};
};

/**
 * Formats the referral type by capitalizing the first letter and lowercasing the rest.
 * Defaults to 'ROUTINE' if no value is provided.
 *
 * @param referralType - The referral type to format (e.g., 'URGENT', 'routine').
 * @returns A formatted referral type string (e.g., 'Urgent', 'Routine').
 */
export const formatReferralType = (referralType: ReferralType = ReferralType.ROUTINE) => {
  // Replace underscores with spaces, then capitalize each word
  return referralType
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Returns the display text for a boolean question based on its ID and specialty configuration.
 *
 * @param questionId - The ID of the boolean question to look up.
 * @param specialtyConfig - Optional specialty configuration object that may contain boolean questions.
 * @returns The display text for the question if found, otherwise returns the question ID itself.
 *
 * Example:
 * getBooleanQuestionText("bleeding", config) → "Does the patient experience bleeding?"
 */
export const getBooleanQuestionText = (
  questionId: string,
  specialtyConfig: SpecialtyFormConfig | undefined
): string => {
  if (!specialtyConfig?.booleanQuestions) return questionId;
  const question = specialtyConfig.booleanQuestions.find(q => q.id === questionId);
  return question?.text || questionId;
};

/**
 * Returns the display label for a date field based on its ID and specialty configuration.
 *
 * @param fieldId - The ID of the date field to look up.
 * @param specialtyConfig - Optional specialty configuration object that may contain date fields.
 * @returns The label for the field if found, otherwise returns the field ID itself.
 *
 * Example:
 * getDateFieldLabelLocal("lastVisit", config) → "Date of Last Visit"
 */
export const getDateFieldLabelLocal = (
  fieldId: string,
  specialtyConfig: SpecialtyFormConfig | undefined
): string => {
  if (!specialtyConfig?.dateFields) return fieldId;
  const field = specialtyConfig.dateFields.find(f => f.id === fieldId);
  return field?.label || fieldId;
};

/**
 * Formats a URL by decoding URL-encoded characters to make it user-friendly.
 * Handles cases where the URL might be null or undefined.
 *
 * @param url - The URL to format (can be null or undefined)
 * @returns A user-friendly formatted URL or 'Unknown' if the URL is invalid
 *
 * @example
 * formatUrl("http%3A%2F%2Flocalhost%3A5173%2Ftest-embed.html") → "http://localhost:5173/test-embed.html"
 * formatUrl(null) → "Unknown"
 */
export const formatUrl = (url: string | null | undefined): string => {
  if (!url) return 'Unknown';

  try {
    return decodeURIComponent(url);
  } catch (error) {
    // If decoding fails, return the original URL
    return url;
  }
};

export function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
